import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  toggleAddModal,
  setPage,
  setPageSize,
  setMaxPages,
  setSorting,
  setCheckedItems,
  setAddEventMode,
  setModalViewActualTab,
  toggleNeedRefresh,
  setSearchValue,
  setKeyword,
  setFilterQuery,
} from '../../store/eventsSlice';
import {
  getHeaders,
  headerWidths,
  tabsConfig,
  buttonsConfig,
  leftSideConfig,
  empty,
} from './eventsTableConfig.js';
import { fetchUsers } from './eventsTableService.js';
import Table from '../../components/list2/table';
import Pagination from '../../components/components/pagination/pagination.jsx';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import LineWithButtons from '../../components/components/line_with_buttons/lineWithButtons.jsx';
import UniversalLineWithComponents from '../../components/components/universal_line_with_components/universalLineWithComponents.jsx';
import CustomInput from '../../components/components/custom_input/customInput.jsx';
import { ReactComponent as SearchIcon } from '../../components/icons/search.svg';
import ApiService from '../../components/api_services/apiService';

import AddEvent from './addEvent';

import './Events.css';

function Events() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const totalPages = useSelector((state) => state.events.maxPages);
  const totalCount = useSelector((state) => state.events.totalCount);
  const currentPage = useSelector((state) => state.events.currentPage);
  const pageSize = useSelector((state) => state.events.pageSize || 10);
  const role = useSelector((state) => state.events.role);
  const sortBy = useSelector((state) => state.events.sortBy);
  const sortDirection = useSelector((state) => state.events.sortDirection);
  const checkedItems = useSelector((state) => state.events.checkedItems);
  const needRefresh = useSelector((state) => state.events.needRefresh);
  const keyword = useSelector((state) => state.events.keyword);
  const searchValue = useSelector((state) => state.events.searchValue);
  const filterQuery = useSelector((state) => state.events.filterQuery);
  const addModalOpen = useSelector((state) => state.events.addModalOpen);
  const editModalOpen = useSelector((state) => state.events.editModalOpen);

  const [rows, setRows] = useState([]);
  const sortArrowColorActive = '#060606';
  const sortArrowColorInactive = '#06060650';
  const deleteConfirmationMessage = 'Usunięte zostaną tylko te wydarzenia, na które nie zostały utworzone paczki. Pozostałe możesz jedynie deaktywować.';

  const EventStatusToggle = ({ value, onChange }) => {
    const options = [
      { key: 'ACTIVE', label: 'Aktywne' },
      { key: 'NOTACTIVE', label: 'Nieaktywne' },
      { key: 'PENDING', label: 'Oczekujące' },
      { key: 'PENDING_EDITS', label: 'Zmiany danych' },
      { key: 'ALL', label: 'Wszystkie' },
    ];
    const currentIndex = Math.max(0, options.findIndex((option) => option.key === value));
    const currentOption = options[currentIndex];
    const nextOption = options[(currentIndex + 1) % options.length];
    const toneClass = currentOption.key === 'ALL'
      ? 'event-status--all'
      : currentOption.key === 'ACTIVE'
        ? 'event-status--active'
        : currentOption.key === 'PENDING'
          ? 'event-status--pending'
          : currentOption.key === 'PENDING_EDITS'
            ? 'event-status--data-change'
          : 'event-status--inactive';

    return (
      <button
        type="button"
        className={`event-status-toggle-btn event-status ${toneClass}`}
        onClick={() => onChange(nextOption.key)}
        aria-label={`Filtr statusu: ${currentOption.label}`}
        title={`Filtr: ${currentOption.label} (kliknij, aby przełączyć)`}
      >
        {currentOption.label}
      </button>
    );
  };

  const handleDeleteEvents = async (ids) => {
    if (!ids || ids.length === 0) {
      return;
    }

    const isConfirmed = window.confirm(deleteConfirmationMessage);
    if (!isConfirmed) {
      return;
    }

    try {
      await ApiService.deleteEvents(ids);
      dispatch(setCheckedItems([]));
      dispatch(toggleNeedRefresh());
    } catch (error) {
      console.error('Błąd usuwania wydarzeń:', error);
    }
  };

  const handleDeleteSingleEvent = (id) => {
    handleDeleteEvents([id]);
  };

  const handleDeleteSelectedEvents = () => {
    handleDeleteEvents(checkedItems);
  };

  const handleToggleAddModal = () => {
    dispatch(toggleAddModal());
  };

  const addModalSetTrue = () => {
    dispatch(setAddEventMode(true));
    dispatch(setModalViewActualTab('Dane podstawowe'));
  };

  useEffect(() => {
    const requestedFilter = location?.state?.filterQuery;
    if (requestedFilter) {
      dispatch(setFilterQuery(requestedFilter));
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location?.state?.filterQuery, location.pathname, navigate, dispatch]);

  useEffect(() => {
    dispatch(setPage(1));
  }, [filterQuery, dispatch]);

  useEffect(() => {
    const loadEvents = async () => {
      const tokensValid = await ApiService.checkTokenExpiration();
      if (!tokensValid) {
        return;
      }
      await fetchUsers(
        sortBy,
        sortDirection,
        currentPage,
        dispatch,
        setMaxPages,
        setRows,
        checkedItems,
        keyword,
        handleDeleteSingleEvent,
        filterQuery,
        pageSize
      );
    };
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, role, sortBy, sortDirection, checkedItems, keyword, needRefresh, filterQuery, dispatch]);

  const headers = getHeaders(
    sortBy,
    sortDirection,
    dispatch,
    setSorting,
    sortArrowColorActive,
    sortArrowColorInactive,
    rows,
    checkedItems
  );

  const paginationProps = {
    reduxSelector: (state) => state.events.currentPage,
    reduxAction: setPage,
    totalPages,
    maxVisibleButtons: 5,
    reduxPageSizeSelector: (state) => state.events.pageSize,
    reduxPageSizeAction: setPageSize,
    pageSizeOptions: [10, 25, 50, 100],
  };

  const rightSideConfig2 = [
    {
      component: Pagination,
      props: paginationProps,
    },
  ];

  const searchRightSide = [
    {
      component: EventStatusToggle,
      props: {
        value: filterQuery,
        onChange: (nextFilter) => dispatch(setFilterQuery(nextFilter)),
      },
    },
    {
      component: CustomInput,
      props: {
        icon: SearchIcon,
        placeholder: 'Wyszukaj',
        hoverClass: 'hover-effect',
        borders: 'LRBT',
        bordersClassPrefix: 'border-black-',
        baseClass: 'custom-input',
        activeClass: '',
        value: searchValue,
        onChange: (e) => {
          e.preventDefault();
          const value = e.target.value;
          dispatch(setSearchValue(value));
          if (value.length >= 3) {
            dispatch(setKeyword(value));
          } else {
            dispatch(setKeyword(null));
          }
        },
      },
    },
  ];

  return (
    <div>
      {(addModalOpen || editModalOpen) && <AddEvent />}
      <Menu />
      <Content>
        <LineWithButtons tabs={tabsConfig()} buttons={buttonsConfig(handleToggleAddModal, addModalSetTrue)} />
        <div className="table-container">
          <UniversalLineWithComponents leftSide={empty} rightSide={searchRightSide} />
          <Table
            headers={headers}
            rows={rows}
            columnWidths={headerWidths}
            idViewing={false}
            idArrayId={1}
            cellClass2="cell-actions"
            cellClass2ConditionList={[2]}
          />
          <div className="table-results-info">
            <span className="table-results-text">Liczba wyników: {totalCount ?? 0}</span>
          </div>
          <UniversalLineWithComponents
            leftSide={leftSideConfig(handleDeleteSelectedEvents, checkedItems)}
            rightSide={rightSideConfig2}
          />
        </div>
      </Content>
    </div>
  );
}

export default Events;
